/**
 * Projects list (`/projects`).
 *
 * Filter chips on top (lifecycle state, overdue only, search input).
 * Below: data table with one row per project, click to drilldown.
 *
 * Query params survive in the URL so filters are sharable / back-button
 * friendly. TanStack Router's search params handle this.
 */
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { AlertTriangle, ClipboardList, Plus, Search, X } from 'lucide-react';
import { formatDate } from '@procurement/shared';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { DataTable, type ColumnDef } from '@/components/ui/data-table';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorPanel } from '@/components/ui/error-panel';
import { ExportCsvButton } from '@/components/ui/export-csv-button';
import { FilterChip, FilterChipGroup } from '@/components/ui/filter-chip';
import { Input } from '@/components/ui/form-field';
import { PageHeader } from '@/components/ui/page-header';
import { ProjectNumber } from '@/components/ui/project-number';
import { SortableHeader } from '@/components/ui/sortable-header';
import { Spinner } from '@/components/ui/spinner';
import { StatusBadge } from '@/components/ui/status-badge';
import { NewProjectModal } from './new-project-modal';
import { usePageTitle } from '@/lib/use-page-title';
import {
  type ProjectLifecycleState,
  type ProjectsSortKey,
  type VisibilityRow,
  useProjectsList,
} from '@/lib/visibility-queries';

// Lifecycle states that warrant a filter chip — the rest are uncommon enough
// that the dropdown isn't worth it for v1.
const FILTERABLE_STATES: { value: ProjectLifecycleState; label: string }[] = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'PENDING_PARTS_APPROVAL', label: 'Pending approval' },
  { value: 'DFA_RECEIVED', label: 'New (DFA)' },
  { value: 'AWAITING_PAYMENT', label: 'Awaiting payment' },
];

export function ProjectsListPage() {
  usePageTitle('Projects');
  const search = useSearch({ strict: false }) as {
    lifecycleState?: ProjectLifecycleState;
    overdueOnly?: boolean;
    q?: string;
    includeClosed?: boolean;
    // Patch 5 — sortable columns
    sortBy?: ProjectsSortKey;
    sortDir?: 'asc' | 'desc';
  };
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState(search.q ?? '');
  const [newOpen, setNewOpen] = useState(false);

  const setFilter = (updates: Partial<typeof search>): void => {
    // Patch 5+ — `replace: true` so filter / sort toggles modify the
    // current history entry instead of pushing new ones. Without this,
    // typing in the search box or flipping a sort column adds entries
    // to the back stack, so when the user later clicks a row and hits
    // Back, they land in a midway filter state instead of the page
    // they came from.
    void navigate({
      to: '/projects',
      search: { ...search, ...updates } as Record<string, unknown>,
      replace: true,
    });
  };

  // Patch 5 — toggle sort. Default sort is updatedAt desc (matches server).
  const sortBy: ProjectsSortKey = search.sortBy ?? 'updatedAt';
  const sortDir: 'asc' | 'desc' = search.sortDir ?? 'desc';
  const toggleSort = (key: ProjectsSortKey): void => {
    if (sortBy === key) {
      setFilter({ sortDir: sortDir === 'asc' ? 'desc' : 'asc' });
    } else {
      setFilter({ sortBy: key, sortDir: 'desc' });
    }
  };

  const query = useProjectsList({
    pageSize: 100,
    lifecycleState: search.lifecycleState,
    overdueOnly: search.overdueOnly,
    excludeClosed: !search.includeClosed,
    search: search.q,
    sortBy,
    sortDir,
  });

  // Patch 5 — build the export URL with the same filters the list is showing.
  // The server endpoint accepts a subset (no lifecycleState / overdueOnly —
  // those are derived, see /api/exports/projects); pass through what it
  // does accept so the CSV honours the filters the user can see.
  const exportHref = (() => {
    const params = new URLSearchParams();
    if (!search.includeClosed) params.set('excludeClosed', 'true');
    if (search.q) params.set('search', search.q);
    const qs = params.toString();
    return qs ? `/api/exports/projects?${qs}` : '/api/exports/projects';
  })();

  const columns: ColumnDef<VisibilityRow>[] = [
    {
      id: 'projectNumber',
      header: (
        <SortableHeader
          label="Project #"
          active={sortBy === 'projectNumber'}
          direction={sortDir}
          onToggle={() => toggleSort('projectNumber')}
        />
      ),
      width: '140px',
      cell: (row) => <ProjectNumber number={row.projectNumber} size="sm" />,
    },
    {
      id: 'projectTitle',
      header: (
        <SortableHeader
          label="Title"
          active={sortBy === 'projectTitle'}
          direction={sortDir}
          onToggle={() => toggleSort('projectTitle')}
        />
      ),
      cell: (row) => (
        <Link
          to="/projects/$projectId"
          params={{ projectId: row.projectId }}
          className="font-medium text-primary hover:underline"
        >
          {row.projectTitle}
        </Link>
      ),
    },
    {
      id: 'clientName',
      header: (
        <SortableHeader
          label="Client"
          active={sortBy === 'clientName'}
          direction={sortDir}
          onToggle={() => toggleSort('clientName')}
        />
      ),
      width: '140px',
      cell: (row) => <span className="text-secondary">{row.clientName}</span>,
    },
    {
      id: 'lifecycle',
      header: 'Status',
      width: '180px',
      cell: (row) => (
        <div className="flex items-center gap-1.5">
          <StatusBadge kind="lifecycle" value={row.lifecycleState} />
          {row.isOverdue ? (
            <Badge tone="danger" className="gap-1">
              <AlertTriangle className="h-3 w-3" />
              Overdue
            </Badge>
          ) : null}
        </div>
      ),
    },
    {
      id: 'parts',
      header: 'Parts',
      numeric: true,
      width: '70px',
      cell: (row) => row.partCount,
    },
    {
      id: 'batches',
      header: 'Batches',
      numeric: true,
      width: '90px',
      cell: (row) =>
        row.overdueBatchCount > 0 ? (
          <span className="text-status-danger">
            {row.activeBatchCount} <span className="text-xs">({row.overdueBatchCount} late)</span>
          </span>
        ) : (
          row.activeBatchCount
        ),
    },
    {
      id: 'deliveryTarget',
      header: (
        <SortableHeader
          label="Effective target"
          active={sortBy === 'effectiveDeliveryTargetDate'}
          direction={sortDir}
          onToggle={() => toggleSort('effectiveDeliveryTargetDate')}
          numeric
        />
      ),
      numeric: true,
      width: '120px',
      cell: (row) => <span className="text-secondary">{formatDate(row.effectiveDeliveryTargetDate)}</span>,
    },
  ];

  const rows = query.data?.items ?? [];

  return (
    <>
      <PageHeader
        title="Projects"
        subtitle={query.data ? `${query.data.total} projects` : ' '}
        actions={
          <div className="flex items-center gap-2">
            <ExportCsvButton
              href={exportHref}
              fallbackFilename="projects.csv"
            />
            <Button
              variant="primary"
              size="default"
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={() => setNewOpen(true)}
            >
              New project
            </Button>
          </div>
        }
      />

      {/* Filter bar */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative w-[280px]">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-tertiary" />
          <Input
            className="h-8 pl-8"
            type="search"
            placeholder="Search by title or number…"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') setFilter({ q: searchInput || undefined });
              if (event.key === 'Escape') {
                setSearchInput('');
                setFilter({ q: undefined });
              }
            }}
            onBlur={() => {
              if (searchInput !== (search.q ?? '')) {
                setFilter({ q: searchInput || undefined });
              }
            }}
          />
        </div>
        <FilterChipGroup>
          {FILTERABLE_STATES.map((state) => {
            const active = search.lifecycleState === state.value;
            return (
              <FilterChip
                key={state.value}
                active={active}
                onClick={() => setFilter({ lifecycleState: active ? undefined : state.value })}
                onClear={() => setFilter({ lifecycleState: undefined })}
              >
                {state.label}
              </FilterChip>
            );
          })}
          <FilterChip
            active={Boolean(search.overdueOnly)}
            onClick={() => setFilter({ overdueOnly: !search.overdueOnly || undefined })}
            onClear={() => setFilter({ overdueOnly: undefined })}
          >
            Overdue only
          </FilterChip>
          <FilterChip
            active={Boolean(search.includeClosed)}
            onClick={() => setFilter({ includeClosed: !search.includeClosed || undefined })}
            onClear={() => setFilter({ includeClosed: undefined })}
          >
            Include closed
          </FilterChip>
        </FilterChipGroup>
        {search.lifecycleState || search.overdueOnly || search.includeClosed || search.q ? (
          <button
            type="button"
            onClick={() => {
              setSearchInput('');
              void navigate({ to: '/projects', search: {} });
            }}
            className="ml-1 inline-flex items-center gap-1 text-xs text-tertiary hover:text-primary"
          >
            <X className="h-3 w-3" /> Clear all
          </button>
        ) : null}
      </div>

      {/* Content */}
      <Card bodyClassName="p-0">
        {query.isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner className="h-6 w-6 text-secondary" />
          </div>
        ) : query.isError || !query.data ? (
          // Patch 5 — error state. Previously rendered as "0 projects".
          <ErrorPanel
            title="Could not load projects"
            onRetry={() => query.refetch()}
          />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<ClipboardList className="h-6 w-6" />}
            title={
              search.q || search.lifecycleState || search.overdueOnly
                ? 'No matching projects'
                : 'No projects yet'
            }
            body={
              search.q || search.lifecycleState || search.overdueOnly
                ? 'Try clearing some filters, or search for a different project number.'
                : 'Click \u201cNew project\u201d to create one from a DFA receipt.'
            }
            action={
              !(search.q || search.lifecycleState || search.overdueOnly) ? (
                // Patch 5 — unambiguous "no projects yet" → direct user to the
                // create flow.
                <Button onClick={() => setNewOpen(true)}>
                  <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
                  New project
                </Button>
              ) : null
            }
          />
        ) : (
          <DataTable<VisibilityRow> columns={columns} rows={rows} rowKey={(row) => row.projectId} />
        )}
      </Card>

      <NewProjectModal open={newOpen} onClose={() => setNewOpen(false)} />
    </>
  );
}
